/**
 * Views any saved report through the report engine. A row that stores no spec is rebuilt by the backend
 * adapter; one it cannot rebuild answers 422 naming the missing field, which is shown as-is.
 */

import React from 'react';
import Alert from '@amzn/awsui-components-core/alert';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import Header from '@amzn/awsui-components-core/header';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import { ReportLoadStats } from 'src/components/star-grid/components/ReportLoadStats';
import { ReportQueryNotices } from 'src/components/star-grid/components/ReportQueryNotices';
import { StarGridLoading } from 'src/components/star-grid/components/StarGridLoading';
import { ReportFilters } from 'src/components/star-grid/components/ReportFilters';
import { ReportResultGrid } from 'src/components/star-grid/components/ReportResultGrid';
import { ReportVersionSelector } from 'src/components/star-grid/components/ReportVersionSelector';
import { CompareVersionSelector } from 'src/components/star-grid/components/CompareVersionSelector';
import { useStarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { useReportView } from 'src/components/star-grid/hooks/useReportView';

export interface ReportViewProps {
  readonly reportId: number;
  readonly onBack?: () => void;
}

export const ReportView: React.FC<ReportViewProps> = ({ reportId, onBack }) => {
  const text = useStarGridText();
  const view = useReportView(reportId, text);
  const { report, spec, drill } = view;

  if (report.loading) return <StarGridLoading label={text('reportLoading')} />;
  if (!spec)
    return (
      <Alert type="error" action={onBack && <Button onClick={onBack}>{text('backToReports')}</Button>}>
        {report.error ?? text('reportQueryFailed')}
      </Alert>
    );

  const provenance = spec.adapted_provenance;
  return (
    <SpaceBetween size="m">
      <Header
        variant="h1"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <ReportLoadStats
              durationMs={view.loadDurationMs}
              loading={view.busy}
              serverDurationMs={view.serverDurationMs}
              statsSource={view.statsSource}
              queries={view.queries}
              onExplain={view.explain}
            />
            {onBack && <Button onClick={onBack}>{text('backToReports')}</Button>}
          </SpaceBetween>
        }
        description={provenance ? text('reportAdaptedNotice') : undefined}
      >
        {spec.name}
      </Header>
      {report.error && <Alert type="error">{report.error}</Alert>}
      {drill.error && (
        <Alert type="error" dismissible onDismiss={drill.clearError}>
          {drill.error}
        </Alert>
      )}
      {/* A dropped ex FX column or variance is one the user would otherwise look for and not find. */}
      {provenance && provenance.warnings.length > 0 && (
        <Alert type="info" header={text('reportAdaptedNotice')}>
          <Box>
            <ul>
              {provenance.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </Box>
        </Alert>
      )}
      <ReportFilters
        filters={spec.filters ?? []}
        filterTrees={spec.filter_trees}
        viewControls={spec.view_controls ?? []}
        viewControlActions={view.viewControlActions}
        // Version renders as the snapshot picker, which brings its own chip.
        viewControlNodes={{
          version: (
            <ReportVersionSelector
              label={spec.view_controls?.find((control) => control.id === 'version')?.label ?? text('reportVersion')}
              controlId="stargrid-report-version"
              control={view.version.control}
              choices={view.version.choices}
            />
          ),
          ...Object.fromEntries(
            view.compareVersions.map((control) => [
              control.id,
              <CompareVersionSelector key={control.id} reportId={spec.id} control={control} onPick={view.pickVersion} />
            ])
          )
        }}
        text={text}
        overrides={view.overrides}
        onChange={view.onFilterChange}
        busy={report.running}
        showBy={view.showBy.value}
        availableShowBy={report.grid?.show_by_available}
        onShowByChange={view.showBy.setValue}
        suppressZeroRows={view.suppressZeroRows}
        onSuppressZeroRowsChange={view.setSuppressZeroRows}
      />
      <ReportQueryNotices notices={view.notices} text={text} />
      <ReportResultGrid
        scaleId={view.scaleId}
        // Expansions live outside the query response, which a re-query replaces wholesale.
        response={drill.withChildren(report.grid)}
        text={text}
        loading={view.busy}
        reportName={spec.name}
        rowDrillTargets={drill.dimensions}
        onRowDrillMenu={drill.loadDimensions}
        onRowDrill={(memberId, indent, target) => void drill.drill(memberId, indent, target)}
        isRowExpanded={drill.isExpanded}
        onRowCollapse={drill.collapse}
        onRowToggle={(key, indent) => (drill.isExpanded(key) ? drill.collapse(key) : void drill.drill(key, indent))}
        onOuterToggle={(member, indent) => void drill.toggleOuter(member, indent)}
        isOuterExpanded={drill.isOuterExpanded}
        // Rides the same `filter_overrides` the chips use, so the chip shows what was applied.
        onFilterByRow={(memberId) => view.onFilterChange(spec.row_axis?.dimension ?? '', [memberId])}
      />
    </SpaceBetween>
  );
};
